import React from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import type { ThemeMode } from "../../contexts/DarkModeContext";
import { useDarkModeContext } from "../../contexts/DarkModeContext";

interface DarkModeToggleProps {
  /** Override classes when used on dark backgrounds (e.g. sidebar) */
  className?: string;
}

const MODES: { value: ThemeMode; icon: React.ElementType; label: string; title: string }[] = [
  { value: "system", icon: Monitor, label: "Appareil", title: "Suivre le réglage de l'appareil" },
  { value: "light", icon: Sun, label: "Clair", title: "Mode clair" },
  { value: "dark", icon: Moon, label: "Sombre", title: "Mode sombre" },
];

export function DarkModeToggle({ className }: DarkModeToggleProps) {
  const { themeMode, setThemeMode } = useDarkModeContext();

  return (
    <div
      role="group"
      aria-label="Choix du thème"
      className={
        className ??
        "inline-flex rounded-full p-1 bg-gray-100/90 dark:bg-white/[0.06] border border-gray-200/60 dark:border-white/[0.08] shadow-sm shadow-black/[0.03] dark:shadow-none backdrop-blur-xl transition-all duration-300"
      }
    >
      {MODES.map(({ value, icon: Icon, label, title }) => {
        const isSelected = themeMode === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setThemeMode(value)}
            title={title}
            className={`
              relative flex h-9 w-9 items-center justify-center rounded-full
              transition-all duration-200 ease-out
              focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/50 dark:focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
              ${isSelected ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300"}
            `}
            aria-pressed={isSelected}
            aria-label={label}
          >
            {isSelected && (
              <span
                className="absolute inset-0 rounded-full bg-white dark:bg-white/15 shadow-sm ring-1 ring-black/[0.04] dark:ring-white/10 transition-all duration-200 ease-out"
                aria-hidden
              />
            )}
            <Icon size={18} className="relative z-10 stroke-[1.5]" strokeWidth={1.5} />
          </button>
        );
      })}
    </div>
  );
}
